import React, { useEffect, useMemo, useState } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { Popover } from "@mui/material"
import { formatDate, getSlashDateFormat, now } from "../../../utils/date"
import Dropdown from "../../Dropdown/Dropdown"
import Button from "../../Button/Button"
import { deleteDocuments, getDocument, updateDocuments } from "../../../features/documents/documents"
import { showSnackBar } from "../../Snackbar/Snackbar"
import { useDispatch, useSelector } from "react-redux"
import ConfirmModal from "../../ConfirmModal/ConfirmModal"
import { useMyContextLoading } from "../../LoadingContextProvider/LoadingContextProvider"
import PdfViewModal from "../../PdfViewModal/PdfViewModal"
import { StorageGetItem, USER } from "../../../lib/storage"
import { useIsSupervisor } from "../../../hooks/user"
import { Autocomplete, TextField } from "@mui/material"
import { LuDownload, LuEye, LuEyeOff, LuInfo, LuTrash2 } from "react-icons/lu"
import { TABLE_CARD_INNER } from "../../Table/tableCardClass"
import { signerIpLabel, uploadedSignDocumentLabel } from "../../../utils/ip"
import { signerEvidenceLines } from "./signerEvidence"
import SignatureHistory from "./SignatureHistory"
import SignatureAuditDownload from "./SignatureAuditDownload"
import { selectFeatureFlag } from "../../../features/featureFlags/selectors"
import { DOCUMENTS_AUDIO_FLAG, isAudioResult } from "../../../constants/callRecordings"
import { REQUEST_ERRORS_FILES } from "../../../constants/requestErrorFlags"
import { ESIGN_DOC_NAMES } from "../../../config/featureFlagKeys"
import { esignDocName, isEsignedRow } from "./esignDocName"
import ConfirmDialog from "../../ConfirmDialog/ConfirmDialog"
import { useUiKit } from "../../UiKit/UiKitContext"
import { failureOf } from "../../../utils/errorHelpers"


// Local styled component removed in favor of global theme overrides

const headerCellSx = {
  fontSize: '13px',
  textTransform: 'uppercase',
  fontWeight: 600,
  color: 'text.secondary',
  backgroundColor: 'var(--color-bg-subtle)',
  borderBottom: 1,
  borderColor: 'divider',
  px: '10px',
  py: 0,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
  lineHeight: 1,
}

const bodyCellSx = {
  fontSize: '0.875rem',
  fontWeight: 400,
  borderBottom: 1,
  borderColor: 'divider',
  color: 'text.primary',
  px: '10px',
  py: 0,
  verticalAlign: 'middle',
}

const ACTION_HEAD_PINNED_CLASS = 'sticky right-0 z-[1] shadow-[inset_1px_0_0_var(--color-divider)]'
const ACTION_BODY_PINNED_CLASS = 'sticky right-0 z-[1] bg-surface shadow-[inset_1px_0_0_var(--color-divider)]'

const signedSigners = (row) => (row?.signers || []).filter((signer) => signer?.signed)

const signerIpCell = (row, uploadedTypeId) => uploadedSignDocumentLabel(row, uploadedTypeId)
  ?? signedSigners(row).map(signerIpLabel).join(', ')

const DocsTableList = ({ data = [], deleteDoc, dropdownData, uploadedTypeId, setIsUpdate, isUpdate, existingSubfolders = [], showEvidence = false, canViewDocs = false }) => {
  const dispatch = useDispatch()
  const audioEnabled = useSelector(selectFeatureFlag(DOCUMENTS_AUDIO_FLAG))
  const filesStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_FILES))
  const esignNamesEnabled = useSelector(selectFeatureFlag(ESIGN_DOC_NAMES))
  const [openPopoverInfo, setOpenPopoverInfo] = useState(null)
  const [popoverInfoAnchorEl, setPopoverInfoAnchorEl] = useState(null)
  const [openDeletePaymentModal, setOpenDeletePaymentModal] = useState(false)
  const { triggerLoading } = useMyContextLoading()
  const { confirm: kitConfirm } = useUiKit()

  const [types, setTypes] = useState('')
  const [subfolderValue, setSubfolderValue] = useState('')

  const [openPopoverType, setOpenPopoverType] = useState(null)
  const [popoverTypeAnchorEl, setPopoverTypeAnchorEl] = useState(null)

  const [openPopoverSubfolder, setOpenPopoverSubfolder] = useState(null)
  const [popoverSubfolderAnchorEl, setPopoverSubfolderAnchorEl] = useState(null)

  const [collapsedGroups, setCollapsedGroups] = useState({})

  const groupedData = useMemo(() => {
    const groups = {}
    data.forEach((row) => {
      const key = row.subfolder || ''
      if (!groups[key]) groups[key] = []
      groups[key].push(row)
    })
    const sorted = Object.entries(groups).sort(([a], [b]) => {
      if (!a) return 1
      if (!b) return -1
      return a.localeCompare(b)
    })
    return sorted
  }, [data])

  const hasSubfolders = groupedData.some(([key]) => key !== '')

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const [anchorEl, setAnchorEl] = useState(null)
  const [userRole, setUserRole] = useState('')
  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])
  const isSupervisor = useIsSupervisor()
  const canOpenDocs = userRole !== 'affiliate' || canViewDocs

  const handleClickInfo = (event, id) => {
    if (id === openPopoverInfo) {
      setOpenPopoverInfo(null)
      setPopoverInfoAnchorEl(null)
    } else {
      setOpenPopoverInfo(id)
      setPopoverInfoAnchorEl(event.currentTarget)
    }
  }

  const handleClickType = (event, id) => {
    if (id === openPopoverType) {
      setOpenPopoverType(null)
      setPopoverTypeAnchorEl(null)
    } else {
      setOpenPopoverType(id)
      setPopoverTypeAnchorEl(event.currentTarget)
    }
  }

  const downloadFile = async ({ key }) => {
    try {
      const response = await dispatch(getDocument(key))
      if ((audioEnabled || filesStrict) && response?.error) {
        showSnackBar('error', typeof response.payload === 'string' ? response.payload : 'Unable to download the document')
        return
      }
      const fileData = response?.payload?.data
      const base64Data = audioEnabled && isAudioResult(fileData) ? fileData.downloadUrl : fileData

      if (!base64Data) throw new Error('No data received')

      // Create download link from base64
      const link = document.createElement('a')
      link.href = base64Data
      link.download = key
      document.body.appendChild(link)
      link.click()

      // Cleanup
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading document:', error)
    }
  }


  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (popoverInfoAnchorEl && !popoverInfoAnchorEl.contains(event.target)) {
        setOpenPopoverInfo(null)
        setPopoverInfoAnchorEl(null)
      }
    }

    document.addEventListener('click', handleDocumentClick)

    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [popoverInfoAnchorEl])

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (popoverTypeAnchorEl && !popoverTypeAnchorEl.contains(event.target)) {
        setOpenPopoverType(null)
        setPopoverTypeAnchorEl(null)
      }
    }

    document.addEventListener('click', handleDocumentClick)

    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [popoverTypeAnchorEl])


  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const updateDoc = async (id, data, type) => {
    triggerLoading(true)
    const response = await dispatch(updateDocuments({ documentId: id, data, type }))
    if (response?.payload?.data?.key) showSnackBar("success", "Document Successfully updated")
    setIsUpdate(!isUpdate)
    triggerLoading(false)
  }


  const handleDelete = async (data) => {
    deleteDoc(data)
  }

  const deleteDocOrThrow = async (key) => {
    const response = await dispatch(deleteDocuments(key))
    if (response?.error) {
      throw failureOf(response, 'Unable to delete the document')
    }
    if (response?.payload?.data?.key) showSnackBar("success", "Document Is Successfully Deleted")
    setIsUpdate(!isUpdate)
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]"
        style={{ color: 'var(--color-gray-500)', fontSize: 14 }}>
        No rows
      </div>
    )
  }

  return (
    <div>
      <TableContainer className={`${TABLE_CARD_INNER} overflow-x-auto`}>
        <Table size="small" sx={{ border: 'none', minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ height: 56 }}>
              <TableCell sx={headerCellSx}>Date/User</TableCell>
              <TableCell sx={headerCellSx}>Type</TableCell>
              <TableCell sx={headerCellSx}>Subfolder</TableCell>
              <TableCell sx={headerCellSx}>IP Address</TableCell>
              <TableCell sx={headerCellSx}>View</TableCell>
              <TableCell sx={headerCellSx}>File</TableCell>
              <TableCell sx={headerCellSx}>Seen</TableCell>
              <TableCell sx={headerCellSx}>Show In Portal</TableCell>
              <TableCell sx={headerCellSx} className={ACTION_HEAD_PINNED_CLASS}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedData.map(([groupKey, groupRows]) => (
              <React.Fragment key={groupKey || '__ungrouped'}>
                {hasSubfolders && (
                  <TableRow
                    sx={{ height: 48, cursor: 'pointer', backgroundColor: 'action.hover' }}
                    onClick={() => toggleGroup(groupKey)}
                  >
                    <TableCell colSpan={9} sx={{ ...bodyCellSx, fontWeight: 600, fontSize: '0.875rem' }}>
                      <div className="flex items-center gap-1">
                        <span style={{ fontSize: 12, width: 16, display: 'inline-block' }}>
                          {collapsedGroups[groupKey] ? '▶' : '▼'}
                        </span>
                        {groupKey || 'Ungrouped'}
                        <span className="text-text-secondary font-normal ml-2">({groupRows.length})</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {!collapsedGroups[groupKey] && groupRows.map((row) => (
              <TableRow
                key={row.id}
                hover
                className={row.status === 'Failed' ? 'bg-error-light' : ''}
                sx={{ height: 75, '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <TableCell sx={bodyCellSx}>
                  <div>
                    <div>{row.dateUser?.user?.firstName} {row.dateUser?.user?.lastName}</div>
                    <div className="text-text-secondary">{getSlashDateFormat(row.dateUser?.date)}</div>
                  </div>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  {/* QA 10-19: Change Type cannot retype an e-signed document, so its name is plain text. */}
                  {esignNamesEnabled && isEsignedRow(row) ? (
                    <div className="font-bold">{esignDocName(row)}</div>
                  ) : (
                  <div className="flex w-full">
                    <div className="text-primary-main underline font-bold cursor-pointer"
                      onClick={(event) => handleClickType(event, row.id)}>
                      {row.type?.label}
                    </div>
                    <Popover
                      id={id}
                      open={openPopoverType === row.id}
                      anchorEl={popoverTypeAnchorEl}
                      onClose={handleClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      <div onClick={(e) => e.stopPropagation()}
                        className="flex flex-col justify-center items-start rounded-md shadow-custom gap-3 p-4 pb-6">
                        <span className="text-text-primary text-[14px] font-bold">Change Type</span>
                        <Dropdown
                          setValue={(val) => setTypes(val)}
                          value={types}
                          menuItems={dropdownData}
                          placeholder="Select a Type"
                          className="w-full"
                          variant="standard" />
                        <div className="flex gap-[14px]">
                          <Button onClick={() => {
                            setOpenPopoverType(null)
                            setPopoverTypeAnchorEl(null)
                          }} type="outlined" size="md" width="w-[120px]">Cancel</Button>
                          <Button
                            classes=""
                            hover="hover:bg-primary-main/90" size="md" width="w-[120px]"
                            onClick={async () => {
                              await updateDoc(row.id, { type: dropdownData.find((type) => type.value === types)?._id })
                              setOpenPopoverType(null)
                              setPopoverTypeAnchorEl(null)
                            }}
                          >Apply</Button>
                        </div>
                      </div>
                    </Popover>
                  </div>
                  )}
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <div className="flex w-full">
                    <div className="text-primary-main underline font-bold cursor-pointer"
                      onClick={(event) => {
                        if (row.id === openPopoverSubfolder) {
                          setOpenPopoverSubfolder(null)
                          setPopoverSubfolderAnchorEl(null)
                        } else {
                          setSubfolderValue(row.subfolder || '')
                          setOpenPopoverSubfolder(row.id)
                          setPopoverSubfolderAnchorEl(event.currentTarget)
                        }
                      }}>
                      {row.subfolder || '—'}
                    </div>
                    <Popover
                      open={openPopoverSubfolder === row.id}
                      anchorEl={popoverSubfolderAnchorEl}
                      onClose={() => {
                        setOpenPopoverSubfolder(null)
                        setPopoverSubfolderAnchorEl(null)
                      }}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      <div onClick={(e) => e.stopPropagation()}
                        className="flex flex-col justify-center items-start rounded-md shadow-custom gap-3 p-4 pb-6">
                        <span className="text-text-primary text-[14px] font-bold">Change Subfolder</span>
                        <Autocomplete
                          freeSolo
                          options={existingSubfolders}
                          value={subfolderValue}
                          onChange={(e, newValue) => setSubfolderValue(newValue || '')}
                          onInputChange={(e, newValue) => setSubfolderValue(newValue || '')}
                          sx={{ width: 200 }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Subfolder name" variant="standard" />
                          )}
                        />
                        <div className="flex gap-[14px]">
                          <Button onClick={() => {
                            setOpenPopoverSubfolder(null)
                            setPopoverSubfolderAnchorEl(null)
                          }} type="outlined" size="md" width="w-[120px]">Cancel</Button>
                          <Button
                            hover="hover:bg-primary-main/90" size="md" width="w-[120px]"
                            onClick={async () => {
                              await updateDoc(row.id, { subfolder: subfolderValue })
                              setOpenPopoverSubfolder(null)
                              setPopoverSubfolderAnchorEl(null)
                            }}
                          >Apply</Button>
                        </div>
                      </div>
                    </Popover>
                  </div>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  {showEvidence ? signerIpCell(row, uploadedTypeId) : row.thirdParty?.ipAddress}
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  {canOpenDocs ? (
                    <PdfViewModal iconType={row.imageType} keyName={row.key}
                      viewFileFunc={userRole === 'affiliate' ? undefined : () => updateDoc(row.id, { "review.reviewed": true, "review.reviewedAt": now().toISOString() }, row.__t)} />
                  ) : (<div>-</div>)}
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  {row.name}
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  {/* Same 18px outline set as the row actions, on a token-based tile so
                      it adapts to dark instead of the previous hardcoded #f2f4f7. */}
                  <span
                    className="inline-flex items-center justify-center p-1.5 rounded-md bg-black/[0.04] dark:bg-white/[0.06]"
                    title={row.review?.reviewed ? 'Seen' : 'Not seen'}
                  >
                    {row.review?.reviewed
                      ? <LuEye size={18} className="text-text-secondary" />
                      : <LuEyeOff size={18} className="text-text-secondary" />}
                  </span>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <div className="flex items-center">
                    <Dropdown
                      small={true}
                      setValue={(value) => {
                        userRole !== 'affiliate' ?
                          updateDoc(row.id, { showInPortal: value }, row.__t) :
                          showSnackBar('error', 'Access denied')
                      }}
                      value={row.showInPortal}
                      menuItems={[
                        { label: "Yes", value: true },
                        { label: "No", value: false }
                      ]}
                    />
                  </div>
                </TableCell>

                <TableCell sx={bodyCellSx} className={ACTION_BODY_PINNED_CLASS}>
                  <div className="flex items-center gap-4">
                    {canOpenDocs && <button
                      onClick={() => downloadFile({ key: row.key })}
                      className="flex items-center"
                      title="Download"
                    >
                      <LuDownload size={18} className="text-text-secondary" />
                    </button>}
                    <div className="flex items-center cursor-pointer" onClick={(event) => handleClickInfo(event, row.id)} title="Details">
                      <LuInfo size={18} className="text-text-secondary" />
                    </div>
                    <Popover
                      id={id}
                      open={openPopoverInfo === row.id}
                      anchorEl={popoverInfoAnchorEl}
                      onClose={handleClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <div
                        className="flex flex-col justify-center items-start rounded-md shadow-custom gap-3 p-4 pb-6">
                        <div className="flex flex-col gap-3 text-[12px]">
                          <span className="text-text-primary text-[14px] font-bold">Seen</span>
                          <span className="flex gap-[80px]">
                            <span className="text-text-secondary">At</span>
                            {row.review?.reviewed ?
                              <span>{formatDate(row.review?.reviewedAt)}</span>
                              :
                              <span className="leading-[150%]">This document has not been reviewed</span>
                            }
                          </span>
                        </div>
                        <div className="flex flex-col gap-3 text-[12px]">
                          <h4 className="text-text-primary text-[14px] font-bold">Signed</h4>
                          {showEvidence && uploadedSignDocumentLabel(row, uploadedTypeId) ? (
                            <span>{uploadedSignDocumentLabel(row, uploadedTypeId)}</span>
                          ) : showEvidence && row.status === 'signed' && signedSigners(row).length > 0 ? (
                            signedSigners(row).map((signer, i) => (
                              <div key={i} className="flex flex-col gap-1">
                                <span>{signer?.email || 'N/A'} - {signer?.signedAt ? formatDate(signer?.signedAt) : 'N/A'}</span>
                                {signerEvidenceLines(signer).map(([label, value]) => (
                                  <span key={label} className="flex gap-2">
                                    <span className="text-text-secondary min-w-[80px]">{label}</span>
                                    <span>{value}</span>
                                  </span>
                                ))}
                              </div>
                            ))
                          ) : (
                          <span className="flex gap-[80px]">
                            <span className="text-text-secondary">At</span>
                            {row.status === 'signed' ?
                              row.signers?.map((signer, i) =>
                                <span key={i}>{signer?.email || 'N/A'} - {signer?.signedAt ? formatDate(signer?.signedAt) : 'N/A'}</span>)
                              :
                              <span>This document has not been signed</span>
                            }
                          </span>
                          )}
                          <SignatureAuditDownload enabled={showEvidence} audit={row.signatureAudit} onDownload={downloadFile} />
                          <SignatureHistory enabled={showEvidence} history={row.signatureHistory} onDownload={downloadFile} />
                        </div>
                      </div>
                    </Popover>
                    {(userRole === 'customer' || isSupervisor) && openDeletePaymentModal === row._id && (kitConfirm ?
                      <ConfirmDialog
                        open={Boolean(openDeletePaymentModal)}
                        onClose={() => setOpenDeletePaymentModal(false)}
                        title={row.name ? `Delete the document "${row.name}"?` : "Delete this document?"}
                        impact={["The file is removed from this contact.", "This cannot be undone."]}
                        confirmLabel="Delete document"
                        tone="danger"
                        onConfirm={() => deleteDocOrThrow(row.key)}
                      /> :
                      <ConfirmModal leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
                        title="Are you sure to delete the Document?"
                        modalButtonTitle={"Delete"} classes="" open={openDeletePaymentModal}
                        setOpen={setOpenDeletePaymentModal}
                        functionButton={() => handleDelete(row.key)}>
                        You will not be able to restore it later
                      </ConfirmModal>)}

                    {(userRole === 'customer' || isSupervisor) &&
                      <div className="flex items-center cursor-pointer" onClick={() => setOpenDeletePaymentModal(row._id)} title="Delete">
                        <LuTrash2 size={18} className="text-text-secondary" />
                      </div>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default DocsTableList
